import { useCallback } from "react";
import ReactToPrint from "react-to-print";
import { usePrinterContext } from "../context/printer";
import Button from "../components/Button";
import { useParams } from "react-router-dom";
import fetchRequest from "./fetchRequest";
import { useAlertContext } from "../context/alert";

export default function HandlePrint({
    type = "",
    buttonText = "",
    setOverflowPopup
}) {
    const { menu, layoutState, printPageElement, setPrintLoading } =
        usePrinterContext();
    const { setAlertState, setAlert } = useAlertContext();

    const { menu: menuId } = useParams();

    const { pageLayout } = layoutState;
    const orientation = pageLayout.activeOrientation;

    const getPageContent = useCallback(() => {
        return printPageElement.current;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printPageElement.current]);

    const width =
        orientation === "portrait"
            ? pageLayout.paperSize.width
            : orientation === "landscape"
            ? pageLayout.paperSize.height
            : "";
    const height =
        orientation === "portrait"
            ? pageLayout.paperSize.height
            : orientation === "landscape"
            ? pageLayout.paperSize.width
            : "";

    function setPrintPaperConfigs() {
        const css = `@page {
            size: ${width} ${height};
            margin: 0mm; 
        }`;
        const head = document.head || document.getElementsByTagName("head")[0];
        const style = document.createElement("style");

        style.media = "print";
        style.appendChild(document.createTextNode(css));

        head.appendChild(style);
    }

    const get_PDF_in_base64 = async (htmldoc) => {
        if (!htmldoc) return;

        // Clone to avoid modifying the original document
        const clonedDoc = htmldoc.cloneNode(true);

        // Inject meta tags
        const injectMetaTags = (doc) => {
            const head = doc.querySelector("head");
            if (!head) return;

            const metaTags = [
                { charset: "utf-8" },
                {
                    name: "viewport",
                    content: "width=device-width, initial-scale=1"
                },
                { name: "theme-color", content: "#000000" },
                { name: "robots", content: "noindex" },
                {
                    name: "description",
                    content:
                        "Explore our enticing restaurant menu with a variety of delicious dishes. Join us for an unforgettable dining experience!"
                }
            ];

            metaTags.forEach((metaData) => {
                const meta = document.createElement("meta");
                Object.entries(metaData).forEach(([key, value]) => {
                    meta.setAttribute(key, value);
                });
                head.appendChild(meta);
            });
        };

        // Function to fetch and inject external CSS
        const injectExternalCSS = async (doc) => {
            const links = doc.querySelectorAll('link[rel="stylesheet"]');

            for (let link of links) {
                const href = link.getAttribute("href");
                if (!href) continue;

                try {
                    const response = await fetch(href);
                    const cssText = await response.text();

                    const styleTag = document.createElement("style");
                    styleTag.textContent = cssText;
                    doc.querySelectorAll("head")[0].appendChild(styleTag);

                    // Remove original <link> to prevent duplication
                    link.remove();
                } catch (error) {
                    console.error(`Failed to load CSS from ${href}:`, error);
                }
            }
        };

        injectMetaTags(clonedDoc);
        await injectExternalCSS(clonedDoc);

        // Batch query selectors
        const elements = {
            hideBlocks: clonedDoc.querySelectorAll(".hide_on_print"),
            removeStyle: clonedDoc.querySelectorAll(".printPageElement"),
            removeBorders: clonedDoc.querySelectorAll(".remove_border"),
            removeBackground: clonedDoc.querySelectorAll(".remove_bg"),
            removeClass: clonedDoc.querySelectorAll(".selectedItem"),
            removeMargin: clonedDoc.querySelectorAll(".PagesBlock"),
            addFlex: clonedDoc.querySelectorAll(
                ".PageEditorInner > div, .ReplicatedContent > div"
            ),
            images: clonedDoc.querySelectorAll("img"),
            backgroundImages: clonedDoc.querySelectorAll(".backgroundImage"),
            pages: clonedDoc.querySelectorAll(".PageEditorWrap")
        };

        // Function to apply styles in batch
        const applyStyles = (elements, styles) => {
            elements?.forEach((el) => Object.assign(el.style, styles));
        };

        // Remove/Modify styles
        elements?.hideBlocks?.forEach((el) => (el.style.display = "none"));
        elements?.removeClass?.forEach((el) =>
            el.classList.remove("selectedItem")
        );
        elements?.removeBackground?.forEach(
            (el) => (el.style.backgroundColor = "transparent")
        );
        elements?.removeStyle?.forEach((el) => (el.style = ""));
        applyStyles(elements?.removeBorders, {
            border: "0",
            boxShadow: "none"
        });
        applyStyles(elements?.removeMargin, { marginTop: "0" });
        applyStyles(elements?.addFlex, {
            display: "flex",
            flexDirection: "column"
        });

        // Fix image `srcset` issues
        elements?.images?.forEach((img) => {
            const srcset = img.getAttribute("srcset");
            if (srcset)
                img.setAttribute(
                    "src",
                    srcset.split(",")[0].trim().split(" ")[0]
                );
        });

        // Fix background image `srcset` issues
        elements?.backgroundImages?.forEach((bgEl) => {
            const srcset = bgEl.getAttribute("srcset");
            if (srcset)
                bgEl.style.backgroundImage = `url('${
                    srcset.split(",")[0].trim().split(" ")[0]
                }')`;
        });

        // Upload PDF content
        try {
            const { success, response } = await fetchRequest(
                `/media/upload-pdf-printer/${menu?.identifier}/${menuId}`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        height: height,
                        width: width,
                        htmlContent: clonedDoc.outerHTML
                    })
                }
            );

            if (success && response?.data?.assetUrl) {
                const updatedURL = response.data.assetUrl
                    .replace(/#/g, "%23")
                    .replace(/%/g, "%25")
                    .replace(/\+/g, "%2B");
                window.open(updatedURL, "_blank");
            }
        } catch (error) {
            setAlert({ type: "error", message: error.message || error.error });
            setAlertState(true);
        } finally {
            setPrintLoading(false);
        }
    };

    return (
        <ReactToPrint
            content={getPageContent}
            documentTitle={`${menu.displayName}-${menu.identifier}`}
            onBeforeGetContent={() => {
                setPrintPaperConfigs();
                setPrintLoading(true);
                setOverflowPopup?.(false);
            }}
            removeAfterPrint={false}
            trigger={() => (
                <Button
                    type={type ? type : "primary"}
                    color="orange"
                    innerText={buttonText.length > 0 ? buttonText : "Print"}
                    className="print_btn"
                    icon={buttonText.length > 0 ? "" : "print"}
                />
            )}
            print={async (printIframe) => {
                const document = printIframe.contentDocument;
                if (document) {
                    const html = document.getElementsByTagName("html")[0];
                    get_PDF_in_base64(html);
                } else {
                    setPrintLoading(false);
                }
            }}
            onPrintError={(error) => console.log(error, "error")}
        />
    );
}
